// Lightweight UTM / referrer / landing URL capture.
// - Reads UTM params + referrer + landing URL on first page load per session.
// - Persists in sessionStorage so query-string view switches (?view=client|professional)
//   and internal navigation do not clobber the original source signals.
// - Exposes a single helper that returns a flat object ready to attach to form payloads.

const KEY = "ols_utm_v1";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

const readFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const out = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) out[k] = v;
    });
    return out;
  } catch {
    return {};
  }
};

const loadStored = () => {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const persist = (payload) => {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* sessionStorage may be unavailable; capture is best-effort */
  }
};

// Ensures we have a stored snapshot for this session. Returns the snapshot.
export const ensureAttribution = () => {
  if (typeof window === "undefined") return {};
  const existing = loadStored();
  if (existing) {
    // Merge in any UTM params from the current URL if they weren't captured earlier
    // in the session (e.g., user opened a new tagged link mid-session).
    const fresh = readFromUrl();
    const merged = { ...existing };
    let changed = false;
    UTM_KEYS.forEach((k) => {
      if (fresh[k] && !merged[k]) {
        merged[k] = fresh[k];
        changed = true;
      }
    });
    if (changed) persist(merged);
    return merged;
  }
  const snapshot = {
    ...readFromUrl(),
    referrer_url: (typeof document !== "undefined" && document.referrer) || "",
    landing_url: window.location.href,
    captured_at: new Date().toISOString(),
  };
  persist(snapshot);
  return snapshot;
};

// Convenience: shape ready for form payload / FormData.
export const getAttributionFields = () => {
  const snap = ensureAttribution();
  return {
    utm_source: snap.utm_source || "",
    utm_medium: snap.utm_medium || "",
    utm_campaign: snap.utm_campaign || "",
    utm_term: snap.utm_term || "",
    utm_content: snap.utm_content || "",
    referrer_url: snap.referrer_url || "",
    landing_url: snap.landing_url || "",
  };
};
